/*
 * Lineage Explorer — picks an indicator, walks the chain:
 *   indicator
 *     → numerator/denominator metricId
 *       → buckets that contribute to that metric
 *         → (program, stage, period, orgUnit) provenance
 *
 * Pure read-side derivation from /api/indicators + /api/mutations/buckets;
 * no new endpoint needed for this lens. Phase 8+ can deepen this into
 * source-fact level lineage once the contribution journal is exposed.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";

type Indicator = Awaited<ReturnType<typeof api.listIndicators>>["indicators"][number];
type Bucket = Awaited<ReturnType<typeof api.listBuckets>>["buckets"][number];

export default function LineageExplorer() {
  const [indicators, setIndicators] = useState<Indicator[] | null>(null);
  const [buckets, setBuckets] = useState<Bucket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string>("");

  useEffect(() => {
    Promise.all([api.listIndicators(), api.listBuckets()])
      .then(([i, b]) => { setIndicators(i.indicators); setBuckets(b.buckets); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const picked = useMemo(() => indicators?.find((i) => i.id === pickedId) ?? null, [indicators, pickedId]);

  const numeratorBuckets = useMemo(() => {
    if (!picked || !buckets) return [];
    return buckets.filter((b) => b.metricId === picked.numeratorMetricId);
  }, [picked, buckets]);

  const denominatorBuckets = useMemo(() => {
    if (!picked || !buckets || !picked.denominatorMetricId) return [];
    return buckets.filter((b) => b.metricId === picked.denominatorMetricId);
  }, [picked, buckets]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Lineage explorer</h1>
          <p className="muted">
            Walk an indicator's value back to the buckets that produced it. Picks an indicator → shows numerator + denominator metrics → lists the contributing (program, stage, period, org unit) buckets.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters">
        <label className="transfer-filter transfer-filter-grow">
          Indicator
          <select value={pickedId} onChange={(e) => setPickedId(e.target.value)}>
            <option value="">— pick an indicator —</option>
            {(indicators ?? []).map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </label>
      </section>

      {!picked && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>Pick an indicator above to trace its lineage</h2>
          <p>The lineage trail shows every bucket that contributes to the selected indicator's numerator and denominator. Useful for auditing why an indicator value changed.</p>
        </div>
      )}

      {picked && (
        <>
          <section className="section">
            <div className="section-head">
              <h2>{picked.name}</h2>
              <span className="muted" style={{ fontSize: 12 }}>{picked.id}</span>
            </div>
            <dl className="dl-grid">
              <dt>Numerator metric</dt><dd className="mono">{picked.numeratorMetricId}</dd>
              <dt>Denominator metric</dt><dd className="mono">{picked.denominatorMetricId ?? "— (sum only)"}</dd>
              <dt>Workspace</dt><dd className="mono">{picked.workspaceId ?? "—"}</dd>
            </dl>
          </section>

          <section className="section">
            <div className="section-head"><h2>Numerator buckets ({numeratorBuckets.length})</h2></div>
            {numeratorBuckets.length === 0 ? (
              <div className="empty-state-card">
                <h2 style={{ marginTop: 0 }}>No buckets contribute to the numerator</h2>
                <p>The metric ID <code>{picked.numeratorMetricId}</code> isn't represented in the bucket store. Check that the underlying TallyMetric has been generated and that tracker data has been ingested.</p>
              </div>
            ) : (
              <BucketTable buckets={numeratorBuckets} />
            )}
          </section>

          {picked.denominatorMetricId && (
            <section className="section">
              <div className="section-head"><h2>Denominator buckets ({denominatorBuckets.length})</h2></div>
              {denominatorBuckets.length === 0 ? (
                <div className="empty-state-card">
                  <h2 style={{ marginTop: 0 }}>No buckets contribute to the denominator</h2>
                  <p>Denominator <code>{picked.denominatorMetricId}</code> resolves to zero — indicator preview will reflect this.</p>
                </div>
              ) : (
                <BucketTable buckets={denominatorBuckets} />
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function BucketTable({ buckets }: { buckets: Bucket[] }) {
  return (
    <table className="transfer-table">
      <thead>
        <tr>
          <th>Program</th>
          <th>Stage</th>
          <th>Period</th>
          <th>Org unit</th>
          <th style={{ textAlign: "right" }}>Value</th>
        </tr>
      </thead>
      <tbody>
        {buckets.sort((a, b) => b.value - a.value).slice(0, 100).map((b) => (
          <tr key={b.key}>
            <td className="mono" style={{ fontSize: 11 }}>{b.programId}</td>
            <td className="mono" style={{ fontSize: 11 }}>{b.stageId ?? "—"}</td>
            <td className="mono" style={{ fontSize: 11 }}>{b.periodId}</td>
            <td className="mono" style={{ fontSize: 11 }}>{b.orgUnitId}</td>
            <td style={{ textAlign: "right" }}>{Math.round(b.value).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
